import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { z } from "zod"
import { UNIT_STATUSES } from "@/inventory"
import {
  UnitFilters,
  type UnitFilterValues,
} from "@/inventory/components/unit-filters"
import { downloadBase64File } from "@/inventory/lib/download"
import {
  exportUnitsCsvFn,
  exportUnitsExcelFn,
  exportUnitsPdfSummaryFn,
  listCarriersFn,
  listProductsFn,
  listVendorsFn,
} from "@/inventory/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

const exportSearchSchema = z.object({
  type: z.enum(["sim", "device"]).optional().catch(undefined),
  status: z.enum(UNIT_STATUSES).optional().catch(undefined),
  carrierId: z.coerce.number().int().positive().optional().catch(undefined),
  vendorId: z.coerce.number().int().positive().optional().catch(undefined),
  productId: z.coerce.number().int().positive().optional().catch(undefined),
  serialQuery: z.string().optional().catch(undefined),
})

export type ExportSearch = z.infer<typeof exportSearchSchema>

export const Route = createFileRoute("/_inventory-layout/inventory/export")({
  validateSearch: (search) => exportSearchSchema.parse(search),
  component: ExportPage,
})

type ExportKind = "csv" | "excel" | "pdf"

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== "") {
      next[key] = value
    }
  }
  return next as Partial<T>
}

function ExportPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [lastExport, setLastExport] = useState<ExportKind | null>(null)

  const filterValues: UnitFilterValues = {
    type: search.type,
    status: search.status,
    carrierId: search.carrierId,
    vendorId: search.vendorId,
    productId: search.productId,
    serialQuery: search.serialQuery,
  }

  const exportParams = useMemo(
    () =>
      stripUndefined({
        type: search.type,
        status: search.status,
        carrierId: search.carrierId,
        vendorId: search.vendorId,
        productId: search.productId,
        serialQuery: search.serialQuery,
      }),
    [search],
  )

  const carriersQuery = useQuery({
    queryKey: ["carriers", { includeInactive: true }],
    queryFn: () => listCarriersFn({ data: { includeInactive: true } }),
  })

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { includeInactive: true }],
    queryFn: () => listVendorsFn({ data: { includeInactive: true } }),
  })

  const productsQuery = useQuery({
    queryKey: ["products", { includeInactive: true }],
    queryFn: () => listProductsFn({ data: { includeInactive: true } }),
  })

  function setFilters(values: UnitFilterValues) {
    void navigate({
      search: stripUndefined({ ...values }),
    })
  }

  const exportMutation = useMutation({
    mutationFn: async (kind: ExportKind) => {
      const data = exportParams
      if (kind === "csv") {
        return { kind, payload: await exportUnitsCsvFn({ data }) }
      }
      if (kind === "excel") {
        return { kind, payload: await exportUnitsExcelFn({ data }) }
      }
      return { kind, payload: await exportUnitsPdfSummaryFn({ data }) }
    },
    onMutate: (kind) => {
      setError(null)
      setLastExport(kind)
    },
    onSuccess: ({ payload }) => {
      downloadBase64File(payload)
    },
    onError: (err) => {
      setError(getErrorMessage(err))
    },
  })

  const isExporting = exportMutation.isPending
  const activeKind = isExporting ? lastExport : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Export</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Filter units, then download CSV, Excel, or a PDF summary of matching
          inventory.
        </p>
      </div>

      <UnitFilters
        values={filterValues}
        carriers={carriersQuery.data ?? []}
        vendors={vendorsQuery.data ?? []}
        products={productsQuery.data ?? []}
        onChange={setFilters}
      />

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">CSV</CardTitle>
            <CardDescription>
              Spreadsheet-friendly unit rows for filtered inventory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("csv")}
            >
              {activeKind === "csv" ? "Downloading…" : "Download CSV"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Excel</CardTitle>
            <CardDescription>
              .xlsx workbook with the same filtered unit data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("excel")}
            >
              {activeKind === "excel" ? "Downloading…" : "Download Excel"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">PDF summary</CardTitle>
            <CardDescription>
              Compact PDF summary of matching units and applied filters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("pdf")}
            >
              {activeKind === "pdf" ? "Downloading…" : "Download PDF"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
